// Transform Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  Transform as TransformIcon,
  SwapHoriz as MapIcon,
  FilterList as FilterIcon,
  Functions as AggregateIcon
} from '@mui/icons-material';

interface TransformNodeData {
  label: string;
  description?: string;
  transformType?: 'map' | 'filter' | 'aggregate' | 'custom';
  status?: 'idle' | 'running' | 'completed' | 'error';
  inputCount?: number;
  outputCount?: number;
  transformations?: any[];
}

const TransformNode: React.FC<NodeProps<TransformNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getTransformIcon = () => {
    switch (data.transformType) {
      case 'map':
        return <MapIcon />;
      case 'filter':
        return <FilterIcon />;
      case 'aggregate':
        return <AggregateIcon />;
      default:
        return <TransformIcon />;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'running':
        return '#E3F2FD';
      case 'completed':
        return '#E8F5E9';
      case 'error':
        return '#FFEBEE';
      default:
        return '#F3E5F5';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'running':
        return '#2196F3';
      case 'completed':
        return '#4CAF50';
      case 'error':
        return '#F44336';
      default:
        return '#9C27B0';
    }
  };

  const formatTransformType = (type?: string) => {
    if (!type) return 'Transform';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />
      
      {/* Hexagonal shape */}
      <Box
        sx={{
          position: 'relative',
          width: 140,
          height: 80,
          clipPath: 'polygon(20% 0%, 80% 0%, 100% 50%, 80% 100%, 20% 100%, 0% 50%)',
          backgroundColor: getStatusColor(),
          border: `3px solid ${getBorderColor()}`,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 6 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            boxShadow: 8,
            transform: 'scale(1.05)'
          }
        }}
      >
        {/* Data flow animation for running state */}
        {data.status === 'running' && (
          <>
            <Box
              sx={{
                position: 'absolute',
                left: '10%',
                top: '50%',
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: getBorderColor(),
                animation: 'flowLeft 2s ease-in-out infinite'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                right: '10%',
                top: '50%',
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: getBorderColor(),
                animation: 'flowRight 2s ease-in-out infinite'
              }}
            />
          </>
        )}

        {/* Content */}
        <Box
          sx={{
            textAlign: 'center',
            px: 2,
            py: 1
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              color: getBorderColor(),
              mb: 0.5,
              animation: data.status === 'running' ? 'pulse 1.5s ease-in-out infinite' : 'none'
            }}
          >
            {getTransformIcon()}
          </Box>

          {/* Label */}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              lineHeight: 1,
              display: 'block',
              mb: 0.5
            }}
          >
            {data.label}
          </Typography>

          {/* Transform type */}
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              color: 'text.secondary'
            }}
          >
            {formatTransformType(data.transformType)}
          </Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
        {data.inputCount !== undefined && (
          <Chip
            label={`In: ${data.inputCount}`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              backgroundColor: `${getBorderColor()}20`,
              color: getBorderColor()
            }}
          />
        )}
        {data.outputCount !== undefined && (
          <Chip
            label={`Out: ${data.outputCount}`}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.65rem',
              backgroundColor: `${getBorderColor()}20`,
              color: getBorderColor()
            }}
          />
        )}
      </Box>

      {/* Transformations count */}
      {data.transformations && data.transformations.length > 0 && (
        <Box sx={{ mt: 0.5, textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              color: 'text.secondary'
            }}
          >
            {data.transformations.length} transformation{data.transformations.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />

      {/* Processing indicator */}
      {data.status === 'running' && (
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: '#2196F3',
            animation: 'pulse 1s ease-in-out infinite',
            zIndex: 10
          }}
        />
      )}

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.7;
            }
          }
          
          @keyframes flowLeft {
            0% {
              left: 10%;
              opacity: 0;
            }
            50% {
              left: 50%;
              opacity: 1;
            }
            100% {
              left: 90%;
              opacity: 0;
            }
          }
          
          @keyframes flowRight {
            0% {
              right: 90%;
              opacity: 0;
            }
            50% {
              right: 50%;
              opacity: 1;
            }
            100% {
              right: 10%;
              opacity: 0;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(TransformNode);